import React from "react";
import { NeoButton, NeoInput, NeoBadge } from "../../../../../components/Common";
import { TRAINING_METHODS, WORKOUT_TYPES } from "../../constants";
import { calculateSwimBlockMeters } from "../../domain/swimming/calculations";
import { WOD_FORMATS } from "../../domain/wod/constants";
import { RUNNING_SESSION_TYPES } from "../../domain/running/constants";

/**
 * Retorna a config do método pelo value.
 */
const getMethodConfig = (methodValue) =>
    TRAINING_METHODS.find(m => m.value === methodValue) || TRAINING_METHODS[0];

/**
 * Cabeçalho de um bloco de treino.
 * Exibe: badge numérico, nome, tipo de treino, método (musculação),
 * badges de modalidade (WOD/Running/Swimming) e ações (duplicar/deletar).
 *
 * @param {Object}   block             - Dados do bloco (formik values)
 * @param {number}   blockIndex        - Índice do bloco na lista
 * @param {string}   namePrefix        - Prefixo do campo formik (e.g. "blocks")
 * @param {string}   blockWorkoutType  - Tipo de treino efetivo do bloco
 * @param {Function} handleChange      - Formik handleChange
 * @param {Array}    visibleWorkoutTypes - Tipos de treino visíveis pro tenant
 * @param {Function} onDuplicate       - Callback para duplicar o bloco
 * @param {Function} onDelete          - Callback para remover o bloco
 * @param {Function} onSaveWodBenchmark - Callback para salvar WOD benchmark
 * @param {Function} onSaveRunningSession - Callback para salvar sessão de corrida
 */
const BlockHeader = ({
    block,
    blockIndex,
    namePrefix,
    blockWorkoutType,
    handleChange,
    visibleWorkoutTypes = WORKOUT_TYPES,
    onDuplicate,
    onDelete,
    onSaveWodBenchmark,
    onSaveRunningSession,
}) => {
    const isStrength = blockWorkoutType === "strength";
    const isSwimming = blockWorkoutType === "swimming";
    const isWod = blockWorkoutType === "wod";
    const isRunning = blockWorkoutType === "running";
    const method = getMethodConfig(block.method || "normal");
    const swimMeters = isSwimming ? calculateSwimBlockMeters(block) : 0;

    return (
        <div className="d-flex justify-content-between align-items-center mb-3 block-header">
            <div className="d-flex align-items-center gap-2">
                <span className="neo-badge neo-badge-chocolate block-number-badge fw-bold text-brand-primary font-size-13">
                    {blockIndex + 1}
                </span>
                <input
                    type="text"
                    name={`${namePrefix}.${blockIndex}.name`}
                    value={block.name || ""}
                    onChange={handleChange}
                    className="border-0 shadow-none fw-bold text-dark p-0 bg-transparent block-name-input"
                    placeholder="Nome do bloco"
                />
            </div>

            <div className="d-flex align-items-center gap-2">
                {/* ── Seletor de Tipo de Treino ── */}
                <NeoInput
                    type="select"
                    name={`${namePrefix}.${blockIndex}.workoutType`}
                    value={blockWorkoutType}
                    onChange={handleChange}
                    size="sm"
                    className="workout-type-select"
                >
                    {visibleWorkoutTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                </NeoInput>

                {/* ── Seletor de Método (só musculação) ── */}
                {isStrength && (
                    <>
                        <NeoInput
                            type="select"
                            name={`${namePrefix}.${blockIndex}.method`}
                            value={block.method || "normal"}
                            onChange={handleChange}
                            size="sm"
                            className="workout-method-select"
                            id={`method-select-${blockIndex}`}
                            title={method.desc}
                        >
                            {TRAINING_METHODS.map(m => (
                                <option key={m.value} value={m.value}>
                                    {m.label}
                                </option>
                            ))}
                        </NeoInput>
                    </>
                )}
                {isWod && (
                    <>
                        <NeoBadge variant="soft-warning">
                            {WOD_FORMATS.find(format => format.value === block.format)?.label || "WOD"}
                        </NeoBadge>
                        <NeoButton
                            variant="soft-warning"
                            size="sm"
                            square
                            icon="mdi-content-save-outline"
                            title="Salvar como WOD Benchmark"
                            onClick={() => onSaveWodBenchmark?.(block)}
                        />
                    </>
                )}
                {isRunning && (
                    <>
                        <NeoBadge variant="soft-success">
                            {RUNNING_SESSION_TYPES.find(type => type.value === block.sessionType)?.label || "Corrida"}
                        </NeoBadge>
                        <NeoButton
                            variant="soft-success"
                            size="sm"
                            square
                            icon="mdi-content-save-outline"
                            title="Salvar sessão de corrida"
                            onClick={() => onSaveRunningSession?.(block)}
                        />
                    </>
                )}
                {isSwimming && (
                    <NeoBadge variant="soft-primary">
                        {swimMeters.toLocaleString("pt-BR")} m
                    </NeoBadge>
                )}
                {(isStrength || isRunning) && (
                    <NeoButton
                        variant="soft-secondary"
                        size="sm"
                        square
                        icon="mdi-content-copy"
                        title="Duplicar bloco"
                        onClick={onDuplicate}
                    />
                )}
                <NeoButton
                    variant="soft-danger"
                    size="sm"
                    square
                    icon="mdi-delete-outline"
                    onClick={onDelete}
                />
            </div>
        </div>
    );
};

export { getMethodConfig };
export default BlockHeader;
